import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { ArrowRight, Quote } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TestimonialsPage = () => {
  const { t, language } = useLanguage();
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await axios.get(`${API}/testimonials`);
        setTestimonials(response.data);
      } catch (error) {
        console.error('Error fetching testimonials:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTestimonials();
  }, []);

  const serviceLabels = {
    closet: language === 'en' ? 'Closet Organization' : 'Organización de Armario',
    decluttering: 'Decluttering',
    newborn: language === 'en' ? 'Newborn Preparation' : 'Preparación para Recién Nacidos',
    rooms: language === 'en' ? 'Room Organization' : 'Organización de Habitaciones',
  };

  return (
    <main data-testid="testimonials-page" className="pt-20">
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-[#F2F0E9]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <span className="font-accent text-lg text-[#8DA399]">
            {t('testimonials.subtitle')}
          </span>
          <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-[#2C3333] mt-4">
            {t('testimonials.title')}
          </h1>
          <p className="mt-6 font-sans text-lg text-[#5C5C5C] max-w-2xl mx-auto">
            {language === 'en'
              ? 'Hear from our clients about how Galene Home has transformed their spaces and daily lives.'
              : 'Escucha de nuestros clientes cómo Galene Home ha transformado sus espacios y su vida diaria.'}
          </p>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="py-16 md:py-24 bg-[#FAFAF9]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block w-8 h-8 border-4 border-[#8DA399] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {testimonials.map((testimonial, index) => (
                <div
                  key={testimonial.id}
                  data-testid={`testimonial-card-${testimonial.id}`}
                  className="bg-[#F2F0E9] p-8 md:p-12 relative group hover:shadow-lg transition-shadow duration-300"
                >
                  {/* Quote Icon */}
                  <div className="absolute top-6 right-6 opacity-10 group-hover:opacity-20 transition-opacity">
                    <Quote className="w-16 h-16 text-[#8DA399]" />
                  </div>

                  {/* Service Tag */}
                  <span className="inline-block px-3 py-1 bg-[#8DA399]/10 text-[#8DA399] font-sans text-sm tracking-wide mb-6">
                    {serviceLabels[testimonial.service]}
                  </span>

                  {/* Testimonial Text */}
                  <blockquote className="font-serif text-lg md:text-xl text-[#2C3333] italic leading-relaxed">
                    {language === 'en' ? testimonial.text_en : testimonial.text_es}
                  </blockquote>

                  {/* Author */}
                  <div className="mt-8 pt-6 border-t border-[#E5E5E0]">
                    <p className="font-sans font-medium text-[#2C3333]">
                      {testimonial.name}
                    </p>
                    <p className="font-sans text-sm text-[#5C5C5C]">
                      {testimonial.location}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Featured Testimonial */}
      {testimonials.length > 0 && (
        <section className="py-24 md:py-32 bg-[#2C3333]">
          <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24 text-center">
            <Quote className="w-12 h-12 text-[#8DA399] mx-auto mb-8 opacity-50" />
            <blockquote className="font-serif text-2xl md:text-3xl lg:text-4xl text-white italic leading-relaxed">
              {language === 'en' ? testimonials[0]?.text_en : testimonials[0]?.text_es}
            </blockquote>
            <div className="mt-10">
              <p className="font-sans font-medium text-white text-lg">
                {testimonials[0]?.name}
              </p>
              <p className="font-sans text-white/60">
                {testimonials[0]?.location}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#8DA399]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-white">
            {language === 'en'
              ? 'Join Our Happy Clients'
              : 'Únete a Nuestros Clientes Felices'}
          </h2>
          <p className="mt-6 font-sans text-lg text-white/80 max-w-2xl mx-auto">
            {language === 'en'
              ? 'Start your journey to a more organized, serene home today.'
              : 'Comienza tu viaje hacia un hogar más organizado y sereno hoy.'}
          </p>
          <div className="mt-10">
            <Link to="/contact">
              <Button
                data-testid="testimonials-cta-button"
                className="bg-white text-[#8DA399] hover:bg-[#F2F0E9] rounded-full px-10 py-6 text-lg shadow-lg"
              >
                {t('services.requestQuote')}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default TestimonialsPage;
